import { useMemo, useState } from "react";
import type { Contact, Org, Partner } from "../types";
import { useData } from "../hooks/useData";
import { fmtDate } from "../components/Primitives";
import ContactForm from "../components/ContactForm";

/** Contacts on a phone: a searchable list, and a card with the buttons you need when you're about to meet or call someone. */

const initials = (n: string) => n.replace(/[^\p{L}\s]/gu, "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
const digits = (s: string) => s.replace(/[^\d+]/g, "");

export default function PhoneContacts({ id, go, partners, openMinutes, newContact, onDone }: {
  id: string | null;
  go: (s: string, id?: string | null) => void;
  partners: Record<string, Partner>;
  openMinutes: (p: { contactId?: string; dealId?: string; orgId?: string } | null) => void;
  newContact?: boolean;
  onDone?: () => void;
}) {
  const { data: contacts, refetch } = useData<Contact[]>("/api/contacts");
  const { data: orgs } = useData<Org[]>("/api/orgs");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Contact | "new" | null>(newContact ? "new" : null);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = contacts || [];
    return s ? all.filter(c => [c.name, c.org_name, c.title, c.email, c.market].some(v => (v || "").toLowerCase().includes(s))) : all;
  }, [contacts, q]);

  const form = editing && (
    <ContactForm contact={editing === "new" ? null : editing} orgs={orgs || []} partners={partners}
      onClose={() => { setEditing(null); onDone?.(); }}
      onSaved={c => { setEditing(null); refetch(); onDone?.(); if (!id) go("contacts", c.id); }} />
  );

  const current = id ? (contacts || []).find(c => c.id === id) : null;
  if (id) {
    if (!contacts) return <div className="ph-pad"><div className="ph-skel" /></div>;
    if (!current) return <div className="ph-pad"><div className="ph-card ph-empty">That contact isn't in the CRM any more.</div></div>;
    return <>{form}<ContactCard c={current} go={go} partners={partners} openMinutes={openMinutes} onEdit={() => setEditing(current)} /></>;
  }

  return (
    <div className="ph-pad">
      {form}
      <div className="ph-searchrow">
        <input className="ph-input" type="search" placeholder={`Search ${contacts?.length || ""} people`} value={q} onChange={e => setQ(e.target.value)} />
        <button className="ph-btn primary sm" onClick={() => setEditing("new")}>New</button>
      </div>
      {!contacts ? <div className="ph-skel" /> : (
        <div className="ph-card">
          {list.slice(0, 80).map(c => (
            <button key={c.id} className="ph-row ph-row-btn" onClick={() => go("contacts", c.id)}>
              <span className="ph-av">{initials(c.name)}</span>
              <span className="ph-row-m"><span className="ph-row-t">{c.name}</span><span className="ph-row-s">{[c.title, c.org_name].filter(Boolean).join(" · ") || c.email || "—"}</span></span>
              <span className="ph-due">›</span>
            </button>
          ))}
          {list.length > 80 && <div className="ph-note">Showing 80 of {list.length} — search to narrow down.</div>}
          {!list.length && <div className="ph-empty">No one matches “{q}”.</div>}
        </div>
      )}
    </div>
  );
}

interface Recent {
  emails: { id: string; subject: string; date: string; inbound: boolean }[];
  minutes: { id: string; title: string; date: string | null; kind: string; deal_id: string | null }[];
  tasks: { id: string; task: string; due: string | null; owner: string; status: string }[];
}

function ContactCard({ c, go, partners, openMinutes, onEdit }: {
  c: Contact; go: (s: string, id?: string | null) => void; partners: Record<string, Partner>;
  openMinutes: (p: { contactId?: string; orgId?: string } | null) => void; onEdit: () => void;
}) {
  const { data: r } = useData<Recent>(`/api/phone/contacts/${c.id}/recent`);
  const phone = c.mobile || c.phone || "";
  const owners = (c.relationship_owners || []).map(o => partners[o]?.name.split(" ")[0] || o);
  const li = c.linkedin_url ? (/^https?:/.test(c.linkedin_url) ? c.linkedin_url : `https://${c.linkedin_url}`) : null;

  return (
    <div className="ph-pad">
      <section className="ph-person">
        <span className="ph-av big">{initials(c.name)}</span>
        <h2>{c.name}</h2>
        <div className="ph-row-s">{[c.title, c.org_name].filter(Boolean).join(" · ")}</div>
        <div className="ph-chips">
          {c.market && <span className="ph-chip flat">{c.market}</span>}
          {owners.length > 0 && <span className="ph-chip flat">Owner: {owners.join(", ")}</span>}
          {c.do_not_email && <span className="ph-chip flat warn">Do not email</span>}
          {(c.newsletters || []).map(n => <span key={n} className="ph-chip flat">{n}</span>)}
        </div>
      </section>

      <div className="ph-cta">
        <a className={phone ? "" : "off"} href={phone ? `tel:${digits(phone)}` : undefined}>Call</a>
        <a className={phone ? "" : "off"} href={phone ? `https://wa.me/${digits(phone).replace(/^\+/, "")}` : undefined} target="_blank" rel="noreferrer">WhatsApp</a>
        <a className={c.email && !c.do_not_email ? "" : "off"} href={c.email && !c.do_not_email ? `mailto:${c.email}` : undefined}>Email</a>
        <a className={li ? "" : "off"} href={li || undefined} target="_blank" rel="noreferrer">LinkedIn</a>
      </div>

      <div className="ph-two">
        <button className="ph-btn primary" onClick={() => openMinutes({ contactId: c.id, orgId: c.org_id || undefined })}>New minutes</button>
        <button className="ph-btn" onClick={onEdit}>Edit</button>
      </div>

      {(c.how_we_know || c.notes) && (
        <div className="ph-card ph-text">
          {c.how_we_know && <p><b>How we know them:</b> {c.how_we_know}</p>}
          {c.notes && <p>{c.notes}</p>}
        </div>
      )}

      {r && r.tasks.length > 0 && <>
        <h2 className="ph-h2">Open tasks</h2>
        <div className="ph-card">
          {r.tasks.map(t => (
            <button key={t.id} className="ph-row ph-row-btn" onClick={() => go("tasks", t.id)}>
              <span className="ph-row-m"><span className="ph-row-t ph-wrap">{t.task}</span><span className="ph-row-s">{partners[t.owner]?.name || t.owner}{t.status === "waiting" ? " · waiting" : ""}</span></span>
              <span className="ph-due">{t.due ? fmtDate(t.due) : ""}</span>
            </button>
          ))}
        </div>
      </>}
      {r && r.emails.length > 0 && <>
        <h2 className="ph-h2">Latest email</h2>
        <div className="ph-card">
          {r.emails.map(e => (
            <button key={e.id} className="ph-row ph-row-btn" onClick={() => go("inbox-all", e.id)}>
              <span className="ph-av">{e.inbound ? "↓" : "↑"}</span>
              <span className="ph-row-m"><span className="ph-row-t">{e.subject || "(no subject)"}</span><span className="ph-row-s">{e.inbound ? "From them" : "To them"}</span></span>
              <span className="ph-due">{e.date ? fmtDate(e.date) : ""}</span>
            </button>
          ))}
        </div>
      </>}
      {r && r.minutes.length > 0 && <>
        <h2 className="ph-h2">Minutes</h2>
        <div className="ph-card">
          {r.minutes.map(m => (
            <button key={m.id} className="ph-row ph-row-btn" onClick={() => (m.deal_id ? go("project", m.deal_id) : go("clients", c.org_id))}>
              <span className="ph-row-m"><span className="ph-row-t">{m.title}</span><span className="ph-row-s">{m.kind}</span></span>
              <span className="ph-due">{m.date ? fmtDate(m.date) : ""}</span>
            </button>
          ))}
        </div>
      </>}
      {c.org_id && <button className="ph-btn ghost" onClick={() => go("clients", c.org_id)}>Open {c.org_name || "the account"} ›</button>}
    </div>
  );
}
